import { default as Table } from 'antd/es/table';
import type { ColumnType, TableProps } from 'antd/es/table';
import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import type { Dispatch, ReactNode } from 'react';

import { TABLE_HEIGHT, TABLE_MIN_WIDTH } from 'hooks/useVirtualList';

import { TotalCount } from '@atoms/TotalCount';

type DataTableProps<T> = {
  total?: number;
  totalDataTestId?: string;
  tableActions?: ReactNode;
  tableActionsClassName?: string;
  headerClassName?: string;
  dataSource?: readonly T[];
  columns: ColumnType<T>[];
  onScroll?: Dispatch<React.UIEvent<HTMLDivElement>>;
  onRowClick?: (record: T) => void;
  isLoading?: boolean;
  rowKey: keyof T & string;
  selectedRow?: T;
  rightPanel?: ReactNode;
  tableDataTestId?: string;
  rowDataTestId?: string;
};

export const DataTable = <T,>({
  total,
  totalDataTestId,
  tableActions,
  tableActionsClassName,
  headerClassName,
  dataSource = [],
  columns,
  onScroll,
  onRowClick,
  isLoading,
  rowKey,
  selectedRow,
  rightPanel,
  tableDataTestId,
  rowDataTestId
}: DataTableProps<T>) => {
  const onRow = useCallback(
    (record: T) => ({
      onClick: onRowClick ? () => onRowClick(record) : undefined,
      'data-testid': rowDataTestId
    }),
    [onRowClick, rowDataTestId]
  );

  const rowStyling = useMemo(() => {
    const selectedKey = selectedRow?.[rowKey];
    const isRowClickingEnabled = Boolean(onRowClick);

    return (record: T) => {
      return cn('h-20 box-border group table-row-centered', {
        'bg-surface-hover': selectedKey && record[rowKey] === selectedKey,
        'cursor-pointer': isRowClickingEnabled
      });
    };
  }, [onRowClick, rowKey, selectedRow]);

  return (
    <div className="mb-12 grid max-h-[80vh] w-full grid-cols-4 gap-6">
      <div
        className={cn(
          'cool-shadow h-fit max-h-[80vh] min-h-0 overflow-auto rounded-2xl',
          {
            'col-span-4': Boolean(rightPanel) === false,
            'col-span-3': Boolean(rightPanel) === true
          }
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between rounded-t-lg border-b px-4 py-5',
            headerClassName
          )}
        >
          <div>
            <TotalCount total={total} data-testid={totalDataTestId} />
          </div>
          <div className={tableActionsClassName}>{tableActions}</div>
        </div>
        <Table
          className="min-h-50"
          data-testid={tableDataTestId}
          scroll={{
            y: TABLE_HEIGHT,
            scrollToFirstRowOnChange: false,
            x: TABLE_MIN_WIDTH
          }}
          rowClassName={rowStyling}
          onScroll={onScroll}
          loading={isLoading}
          rowKey={rowKey}
          onRow={onRow}
          columns={columns}
          dataSource={dataSource as TableProps<T>['dataSource']}
          size="small"
          pagination={false}
          virtual
        />
      </div>
      <div className="max-h-[80vh] overflow-y-auto">{rightPanel}</div>
    </div>
  );
};
